import { useState } from "react";
import { Link } from "react-router-dom";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

import routes from "routes";
import footerRoutes from "footer.routes";

import bgImage from "assets/images/bg-presentation.jpg";

const galleryCategories = [
  {
    title: "Our Students",
    images: [
      { src: "/gallery-1.jpg", alt: "Students in uniform" },
      { src: "/gallery-2.jpg", alt: "Students group photo" },
      { src: "/gallery-3.jpg", alt: "Happy students" },
    ],
  },
  {
    title: "School Events",
    images: [
      { src: "/gallery-4.jpg", alt: "School celebration event" },
      { src: "/gallery-5.jpg", alt: "Student at event" },
      { src: "/gallery-1.jpg", alt: "Students gathering" },
    ],
  },
  {
    title: "Learning Environment",
    images: [
      { src: "/gallery-2.jpg", alt: "Students learning" },
      { src: "/gallery-3.jpg", alt: "Classroom activities" },
      { src: "/gallery-4.jpg", alt: "Active learning" },
    ],
  },
  {
    title: "School Life",
    images: [
      { src: "/gallery-5.jpg", alt: "Daily school life" },
      { src: "/gallery-1.jpg", alt: "Students together" },
      { src: "/gallery-2.jpg", alt: "School community" },
    ],
  },
  {
    title: "Activities & Events",
    images: [
      { src: "/gallery-3.jpg", alt: "School activities" },
      { src: "/gallery-4.jpg", alt: "Fun events" },
      { src: "/gallery-5.jpg", alt: "Student participation" },
    ],
  },
];

function Gallery() {
  const [selectedImage, setSelectedImage] = useState(null);

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  const handleClose = () => {
    setSelectedImage(null);
  };

  return (
    <>
      <DefaultNavbar
        routes={routes}
        action={{
          type: "internal",
          route: "/admissions",
          label: "Enroll Now",
          color: "success",
        }}
        transparent
        light
      />
      <MKBox
        minHeight="75vh"
        width="100%"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.dark.main, 0.6),
              rgba(gradients.dark.state, 0.6)
            )}, url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid
            container
            item
            xs={12}
            lg={8}
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ mx: "auto", textAlign: "center" }}
          >
            <MKTypography
              variant="h1"
              color="white"
              sx={({ breakpoints, typography: { size } }) => ({
                [breakpoints.down("md")]: {
                  fontSize: size["3xl"],
                },
              })}
            >
              Gallery
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.8} mt={1} mb={3}>
              Take a visual tour of Rose of Sharon Primary School
            </MKTypography>
          </Grid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <MKBox component="section" py={6}>
          <Container>
            <Grid container justifyContent="center" sx={{ textAlign: "center", mb: 4 }}>
              <Grid item xs={12} md={8}>
                <MKTypography variant="h3" mb={1}>
                  Our School in Pictures
                </MKTypography>
                <MKTypography variant="body1" color="text">
                  Explore our facilities, events, and the vibrant learning environment at 
                  Rose of Sharon Primary School.
                </MKTypography>
              </Grid>
            </Grid>

            {galleryCategories.map((category, categoryIndex) => (
              <MKBox key={categoryIndex} mb={6}>
                <MKTypography variant="h4" mb={3} sx={{ borderBottom: "2px solid #006747", pb: 1, display: "inline-block" }}>
                  {category.title}
                </MKTypography>
                <Grid container spacing={2}>
                  {category.images.map((image, imageIndex) => (
                    <Grid item xs={12} sm={6} md={4} key={imageIndex}>
                      <MKBox
                        sx={{
                          position: "relative",
                          borderRadius: "lg",
                          overflow: "hidden",
                          cursor: "pointer",
                          "&:hover": {
                            "& img": {
                              transform: "scale(1.05)",
                            },
                            "& .overlay": {
                              opacity: 1,
                            },
                          },
                        }}
                        onClick={() => handleImageClick(image)}
                      >
                        <MKBox
                          component="img"
                          src={image.src}
                          alt={image.alt}
                          width="100%"
                          sx={{
                            height: 250,
                            objectFit: "cover",
                            transition: "transform 0.3s",
                          }}
                        />
                        <MKBox
                          className="overlay"
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(46, 125, 50, 0.7)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            opacity: 0,
                            transition: "opacity 0.3s",
                          }}
                        >
                          <Icon sx={{ color: "#d32f2f", fontSize: "2rem" }}>zoom_in</Icon>
                        </MKBox>
                      </MKBox>
                    </Grid>
                  ))}
                </Grid>
              </MKBox>
            ))}
          </Container>
        </MKBox>

        {/* Call to Action */}
        <MKBox
          component="section"
          py={6}
          sx={{
            background: "linear-gradient(135deg, #006747 0%, #004d35 100%)",
            borderRadius: "xl",
            mx: { xs: 1, lg: 3 },
            my: 4,
            textAlign: "center",
          }}
        >
          <Container>
            <MKTypography variant="h3" color="white" mb={2}>
              Want to See More?
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.9} mb={3}>
              Schedule a visit to our campus and experience our vibrant learning 
              environment firsthand.
            </MKTypography>
            <MKBox
              component={Link}
              to="/contact"
              sx={{
                display: "inline-block",
                backgroundColor: "white",
                color: "#006747",
                px: 4,
                py: 1.5,
                borderRadius: "lg",
                fontWeight: "bold",
                textDecoration: "none",
                transition: "all 0.3s",
                "&:hover": {
                  backgroundColor: "#e8f5e9",
                  transform: "translateY(-2px)",
                },
              }}
            >
              Schedule a Visit
            </MKBox>
          </Container>
        </MKBox>
      </Card>

      {/* Image Modal */}
      <Modal
        open={!!selectedImage}
        onClose={handleClose}
        closeAfterTransition
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Fade in={!!selectedImage}>
          <MKBox
            sx={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
              outline: "none",
            }}
          >
            <IconButton
              onClick={handleClose}
              sx={{
                position: "absolute",
                top: -40,
                right: 0,
                color: "white",
              }}
            >
              <Icon>close</Icon>
            </IconButton>
            {selectedImage && (
              <MKBox
                component="img"
                src={selectedImage.src}
                alt={selectedImage.alt}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "85vh",
                  borderRadius: "lg",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
                }}
              />
            )}
          </MKBox>
        </Fade>
      </Modal>

      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default Gallery;
